import React, { useEffect, useState } from "react";

import { MemberInfo } from "../../../types/member";

import { updateMemberCompany } from "../../../services/memberServices";
import {
  CompanyItem,
  getBuildingCompanyList,
} from "../../../services/buildingServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import HorizontalRule from "../../Rule/Horizontal";
import CommonSelect from "../../Select";
import CommonButton from "../../Button";
import { MyProfileKakaoIcon } from "../../../pages/MyInfo";
import CommonCheckbox from "../../Input/Checkbox";

type Props = {
  memberInfo?: MemberInfo;
  onClose?: () => void;
  onLoad?: () => void;
};

export default function MyInfoChangeCompany({
  memberInfo,
  onClose,
  onLoad,
}: Props) {
  const [companyList, setCompanyList] = useState<CompanyItem[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyItem>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popupComponent, { open }] = useSimplePopup({});

  const validate = () => {
    if (!selectedCompany) {
      open("회사를 선택해주세요");
      return false;
    }

    if (selectedCompany.companyId === memberInfo?.companyId) {
      open("현재 회사와 같은 회사입니다.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (memberInfo?.buildingId) {
      getBuildingCompanyList({ buildingId: memberInfo.buildingId })
        .then((res) => {
          setCompanyList(res.data.items);
        })
        .finally(() => setIsLoading(false));
    }
  }, [memberInfo?.buildingId]);

  return (
    <div>
      {loadingComponent}
      <div className="px-5 pb-5">
        <div className="text-2xl">
          <strong>근무하는 회사</strong>를 변경할게요
        </div>
        <HorizontalRule className="bg-black" />
        <div className="flex flex-col py-2.5 gap-2.5">
          <div className="label-grey-small">현재 근무 위치</div>
          <div className="text-lg font-medium">
            {memberInfo?.buildingName} {memberInfo?.buildingFloorName}
          </div>
        </div>
        <HorizontalRule />
        <div className="flex flex-col py-2.5 gap-2.5">
          <div className="label-grey-small">현재 회사</div>
          <div className="flex justify-between items-between">
            <div className="text-lg font-medium">
              {memberInfo?.companyName ?? "회사를 설정해주세요"}
            </div>
            {memberInfo?.isCompanyCertificated && <CommonCheckbox checked />}
          </div>
        </div>
        <HorizontalRule />
        <div className="flex flex-col py-2.5 gap-2.5">
          <div className="label-grey-small">회사</div>
          <CommonSelect
            defaultValue={
              companyList.length && companyList.length > 0
                ? "회사를 선택해주세요"
                : "건물에 등록된 회사가 없습니다."
            }
            value={selectedCompany?.companyId}
            optionList={companyList.map((company) => ({
              label: company.companyName,
              value: company.companyId,
            }))}
            onChange={(item) => {
              setSelectedCompany(
                companyList.find((company) => company.companyId === item.value)
              );
            }}
            disabled={!companyList.length && companyList.length === 0}
            optionOrder="between"
            optionHeight="140px"
          />
        </div>

        <div className="py-5 flex flex-col gap-2.5 bg-[#f7f7f7] items-center">
          <div className="whitespace-pre-line text-center text-brand-neutral-80 text-sm">
            {"회사 목록에 내 회사가 없다면\n카카오톡으로 문의해 주세요."}
          </div>
          <a href="https://pf.kakao.com/_xhJxkZK/chat">
            <CommonButton
              variant="transparent"
              className="!bg-[#fee500] w-fit px-4 py-3 text-sm text-black font-medium"
            >
              <MyProfileKakaoIcon />
              카카오톡 문의
            </CommonButton>
          </a>
        </div>
      </div>
      <div className="flex">
        <CommonButton
          variant="grey"
          className="bottom-button w-[100px]"
          onClick={() => onClose?.()}
        >
          취소
        </CommonButton>
        <CommonButton
          validation={
            !selectedCompany ||
            selectedCompany.companyId === memberInfo?.companyId
          }
          className="bottom-button"
          onClick={async () => {
            if (!validate()) return;

            setIsLoading(true);
            await updateMemberCompany({
              memberId: memberInfo?.memberId as number,
              companyId: selectedCompany?.companyId as string,
            });
            onLoad?.();
            setIsLoading(false);
            onClose?.();
          }}
        >
          변경 완료
        </CommonButton>
      </div>

      {popupComponent}
    </div>
  );
}
